/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Inbox, Download, FileText, Search, Trash2, ArrowRight } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function Unmatched() {
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [assignRow, setAssignRow] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const [u, c] = await Promise.all([
                api.get("/sync/unmatched"),
                api.get("/clients/"),
            ]);
            setRows(u.data);
            setClients(c.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function discard(id) {
        if (!window.confirm("Permanently delete this unmatched document? NPM won't push it again unless re-triggered.")) return;
        try {
            await api.delete(`/sync/unmatched/${id}`);
            toast.success("Discarded");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div data-testid="unmatched-page">
            <PageHeader
                eyebrow="NPM sync"
                title="Unmatched documents"
                description="Documents pushed by NZTaxation Practice Manager that we couldn't auto-match to a ClientOps client. Review each one and assign it to the correct client so the return shows up in their portal."
                testId="unmatched-header"
            />
            {loading ? (
                <div className="flex items-center justify-center py-16 text-[#5E636E]"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : rows.length === 0 ? (
                <EmptyState
                    icon={Inbox}
                    title="Inbox is clear"
                    description="When NPM pushes a return we can't match to a client (unknown IRD + unknown name) it'll land here for you to review."
                    testId="unmatched-empty"
                />
            ) : (
                <div className="overflow-hidden rounded-lg border border-[#E4E4E7] bg-white" data-testid="unmatched-table">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F5F4EF] text-[10px] uppercase tracking-[0.12em] text-[#5E636E]">
                            <tr>
                                <th className="px-3 py-2 text-left">Received</th>
                                <th className="px-3 py-2 text-left">From NPM</th>
                                <th className="px-3 py-2 text-left">Type</th>
                                <th className="px-3 py-2 text-left">Period</th>
                                <th className="px-3 py-2 text-left">File</th>
                                <th className="px-3 py-2 text-right"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-[#E4E4E7]" data-testid={`unmatched-row-${r.id}`}>
                                    <td className="px-3 py-2 text-[#5E636E] whitespace-nowrap">{new Date(r.received_at).toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium truncate max-w-[16ch]">{r.client_name || "—"}</div>
                                        <div className="text-[10px] text-[#5E636E] font-mono">IRD {r.ird_number || "—"}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium bg-[#0E3B2E]/5 border-[#0E3B2E]/20 text-[#0E3B2E]">{r.job_type}</span>
                                    </td>
                                    <td className="px-3 py-2 text-[#5E636E]">{r.period || "—"}</td>
                                    <td className="px-3 py-2 truncate max-w-[18ch] text-xs">
                                        <button type="button" className="inline-flex items-center gap-1 hover:underline text-[#0E3B2E]" onClick={() => downloadBlob(r.id, r.file_name)} data-testid={`unmatched-download-${r.id}`}>
                                            <FileText className="h-3.5 w-3.5" /> {r.file_name || "document.pdf"}
                                        </button>
                                    </td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">
                                        <Button size="sm" variant="outline" className="h-7 text-xs mr-1" onClick={() => setAssignRow(r)} data-testid={`unmatched-assign-${r.id}`}><ArrowRight className="h-3.5 w-3.5" /> Assign</Button>
                                        <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-700 hover:bg-rose-50" onClick={() => discard(r.id)} data-testid={`unmatched-discard-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {assignRow && (
                <AssignDialog
                    row={assignRow}
                    clients={clients}
                    onClose={() => setAssignRow(null)}
                    onAssigned={() => { setAssignRow(null); load(); }}
                />
            )}
        </div>
    );
}

async function downloadBlob(unmatchedId, fileName) {
    try {
        const res = await api.get(`/sync/unmatched/${unmatchedId}/pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || "document.pdf";
        a.click();
        URL.revokeObjectURL(url);
    } catch (e) {
        toast.error(formatApiError(e));
    }
}

function AssignDialog({ row, clients, onClose, onAssigned }) {
    const [q, setQ] = useState(row.client_name || "");
    const [busy, setBusy] = useState(false);

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return clients.slice(0, 20);
        return clients.filter((c) =>
            (c.name || "").toLowerCase().includes(t) ||
            (c.external_id || "").toLowerCase().includes(t.replace(/[^a-z0-9]/g, ""))
        ).slice(0, 20);
    }, [q, clients]);

    async function assign(client) {
        setBusy(true);
        try {
            const { data } = await api.post(`/sync/unmatched/${row.id}/assign`, { client_id: client.id });
            toast.success(`Filed to ${client.name} — ${data.file_name}`);
            onAssigned?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-lg" data-testid="unmatched-assign-dialog">
                <DialogHeader>
                    <DialogTitle>Assign to client</DialogTitle>
                    <DialogDescription>
                        NPM pushed a <span className="font-medium">{row.job_type}</span> return for period <span className="font-medium">{row.period}</span> with IRD <span className="font-mono">{row.ird_number || "—"}</span> and name <span className="font-medium">{row.client_name || "—"}</span>. Search ClientOps below.
                    </DialogDescription>
                </DialogHeader>
                <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#5E636E]" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or IRD…" className="pl-9" autoFocus data-testid="unmatched-assign-search" />
                </div>
                <div className="mt-2 max-h-72 overflow-y-auto rounded-md border border-[#E4E4E7] divide-y divide-[#E4E4E7]" data-testid="unmatched-assign-list">
                    {filtered.length === 0 ? (
                        <div className="p-4 text-xs text-[#5E636E]">No matches.</div>
                    ) : filtered.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            disabled={busy}
                            onClick={() => assign(c)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-[#FAFAFA] flex items-center justify-between gap-3 disabled:opacity-50"
                            data-testid={`unmatched-assign-target-${c.id}`}
                        >
                            <div className="min-w-0">
                                <div className="font-medium truncate">{c.name}</div>
                                {c.external_id && <div className="text-[10px] text-[#5E636E] font-mono">IRD {c.external_id}</div>}
                            </div>
                            <ArrowRight className="h-3.5 w-3.5 text-[#5E636E]" />
                        </button>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function UnmatchedDownloadButton({ row }) {
    return (
        <button type="button" className="text-xs text-[#0E3B2E] inline-flex items-center gap-1 hover:underline" onClick={() => downloadBlob(row.id, row.file_name)}>
            <Download className="h-3 w-3" /> {row.file_name}
        </button>
    );
}
